import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import {
  AppLayout,
  Header,
  Container,
  SpaceBetween,
  Button,
  Box,
  Badge,
  ColumnLayout,
  StatusIndicator,
  Table,
  TextFilter,
  Pagination,
  ProgressBar,
  Alert,
  Cards,
  Link,
} from "@cloudscape-design/components";
import TopNavigation from "./containers/TopNavigation/TopNavigation";
import Sidebar from "./containers/Sidebar/Sidebar";
import RightSidebar from "./containers/RightSidebar/RightSidebar";
import { ThemeSelector } from "./components/ThemeSelector/ThemeSelector";
import { applyTheme, getCurrentTheme, Theme } from "./themes/themeConfig";
import "./themes/global.themed.css";
import "./themes/navigation-force.css";
import HomePage from "./containers/homePage/HomePage";
import MonitorAlert from "./containers/MonitorAlert/MonitorAlert";
import IntelligentAnalysisReports from "./containers/IntelligentAnalysisReports/IntelligentAnalysisReports";
import ProcessOptimization from "./containers/ProcessOptimization/ProcessOptimization";

// App Content Component with Router
const AppContent: React.FC = () => {
  const location = useLocation();
  const [toolsOpen, setToolsOpen] = useState(false);
  const [chatWidth, setChatWidth] = useState(500);
  const [currentTheme, setCurrentTheme] = useState<Theme>(getCurrentTheme());

  const handleToolsChange = () => {
    setToolsOpen((prevState) => !prevState);
  };

  const handleChatWidthChange = (newWidth: number) => {
    setChatWidth(newWidth);
  };

  useEffect(() => {
    const updateTheme = () => {
      const theme = getCurrentTheme();
      setCurrentTheme(theme);
      applyTheme(theme);
      
      // Apply theme to body and root
      document.body.style.backgroundColor = theme.colors.mainBackground;
      document.body.style.color = theme.colors.textPrimary;
      
      // Dispatch custom event for all components to listen
      window.dispatchEvent(new CustomEvent('themeChanged'));
    };

    // Listen for theme changes
    window.addEventListener('themeChanged', updateTheme);
    
    // Apply initial theme
    updateTheme();

    return () => {
      window.removeEventListener('themeChanged', updateTheme);
    };
  }, []);

  return (
    <div 
      className="awsui"
      style={{
        backgroundColor: currentTheme.colors.mainBackground,
        color: currentTheme.colors.textPrimary,
        minHeight: '100vh'
      }}
    >
      <TopNavigation />
      
      <AppLayout
        navigation={<Sidebar />}
        contentType="table"
        toolsOpen={toolsOpen}
        onToolsChange={handleToolsChange}
        toolsWidth={chatWidth}
        tools={<RightSidebar onWidthChange={handleChatWidthChange} />}
        ariaLabels={{ toolsToggle: 'Open AI Assistant' }}
        content={
          <div
            style={{
              backgroundColor: currentTheme.colors.cardBackground,
              color: currentTheme.colors.textPrimary,
              minHeight: '100%'
            }}
          >
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/monitor" element={<MonitorAlert />} />
              <Route
                path="/analysis"
                element={<IntelligentAnalysisReports />}
              />
              <Route path="/reports" element={<IntelligentAnalysisReports />} />
              <Route path="/optimization" element={<ProcessOptimization />} />
            </Routes>
          </div>
        }
      />
    </div>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    // Initialize theme system on app startup
    const currentTheme = getCurrentTheme();
    applyTheme(currentTheme);
    
    // Apply initial body styling
    document.body.style.backgroundColor = currentTheme.colors.mainBackground;
    document.body.style.color = currentTheme.colors.textPrimary;
    document.body.style.fontFamily = currentTheme.colors.textPrimary ? 'Amazon Ember, Helvetica Neue, Roboto, Arial, sans-serif' : 'inherit';
  }, []);

  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
